import React from 'react'

import {
  Tooltip,
  Button,
  Icon,
  InboxIcon,
  BookmarkRemoveIcon,
  BookmarkAddIcon,
  ShoppingBasketAddIcon,
  ShoppingBasketRemoveIcon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string, bool, func, object, shape,
} from 'prop-types'

import i18n from 'Pods/Shared/I18n'

import { NOOP } from 'Utils/stableDefaults'
import { safeTrackSelectItem } from 'Utils/TrackingUtils'

import WithListingPageContext from '../../WithListingPageContext'

function ModelActions({
  id,
  modelUrl,
  isOnWishlist,
  handleWishlist,
  isWishlistAvailable,
  isWishListActionLoading = false,
  isOnCart,
  handleCart,
  isCartAvailable,
  isCartActionLoading = false,
  fullScreenGallery = false,
  expandButtons = false,
  isPurchased = false,
  itemAttributes,
  downloadData = null,
  onDownload = NOOP,
  locale = 'en',
}) {
  const showLightButtons = fullScreenGallery
  const hasDirectDownload = !!downloadData?.filesHref

  function renderWishlistButton() {
    if (!isWishlistAvailable || isPurchased || hasDirectDownload) {
      return null
    }

    const wishlistIcon = isOnWishlist ? BookmarkRemoveIcon : BookmarkAddIcon
    const tooltipContent = isOnWishlist ? 'Remove from wishlist' : 'Add to wishlist'

    return (
      <Tooltip
        content={tooltipContent}
        id={`wishlist-button-${id}`}
        randomizeID
        offset={{ top: 10 }}
      >
        <Button
          icon={<Icon svgComponent={wishlistIcon} />}
          type="secondary"
          variant="only-icon"
          isActive={isOnWishlist}
          onClick={handleWishlist}
          disabled={isWishListActionLoading}
          customClass={showLightButtons ? 'cgt-model-gallery-new__footer-button--light' : ''}
          expand={expandButtons}
        />
      </Tooltip>
    )
  }

  function renderCartButton() {
    if (!isCartAvailable || isPurchased || hasDirectDownload) {
      return null
    }

    const cartIcon = isOnCart ? ShoppingBasketRemoveIcon : ShoppingBasketAddIcon
    const tooltipContent = isOnCart ? 'Remove from cart' : 'Add to cart'

    return (
      <Tooltip
        content={tooltipContent}
        id={`cart-button-${id}`}
        randomizeID
        offset={{ top: 10 }}
      >
        <Button
          icon={<Icon svgComponent={cartIcon} />}
          type="secondary"
          variant="only-icon"
          isActive={isOnCart}
          onClick={handleCart}
          disabled={isCartActionLoading}
          customClass={showLightButtons ? 'cgt-model-gallery-new__footer-button--light' : ''}
          expand={expandButtons}
        />
      </Tooltip>
    )
  }

  function renderDownloadButton() {
    if (hasDirectDownload) {
      return (
        <Tooltip
          content={i18n.t('listingPage.download.tooltip', { ns: 'rails', lng: locale })}
          id={`download-button-${id}`}
          randomizeID
          offset={{ top: 10 }}
        >
          <Button
            icon={<Icon svgComponent={InboxIcon} />}
            type="secondary"
            variant="only-icon"
            onClick={() => onDownload(downloadData.filesHref)}
            customClass={showLightButtons ? 'cgt-model-gallery-new__footer-button--light' : ''}
            expand={expandButtons}
          />
        </Tooltip>
      )
    }

    if (!isPurchased) {
      return null
    }

    return (
      <Tooltip
        content="Purchased"
        id={`purchased-button-${id}`}
        randomizeID
        offset={{ top: 10 }}
      >
        <Button
          icon={<Icon svgComponent={InboxIcon} />}
          type="secondary"
          variant="only-icon"
          href="/profile/purchases"
          linkAttributes={{ target: '_blank' }}
          customClass={showLightButtons ? 'cgt-model-gallery-new__footer-button--light' : ''}
          expand={expandButtons}
        />
      </Tooltip>
    )
  }

  const handleClick = (event) => {
    event.preventDefault()

    safeTrackSelectItem(itemAttributes)
    if (event.button === 0 && (event.ctrlKey || event.metaKey)) {
      window.open(modelUrl, '_blank')
    } else {
      window.location = modelUrl
    }
  }

  const handleAuxClick = (event) => {
    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      safeTrackSelectItem(itemAttributes)
    }
  }

  return (
    <div className="cgt-model-gallery-new__footer-buttons">
      {renderDownloadButton()}
      {renderWishlistButton()}
      {renderCartButton()}
      <Button
        actionType="button"
        type="primary"
        href={modelUrl}
        onClick={handleClick}
        onAuxClick={handleAuxClick}
        expand={expandButtons}
      >
        Open
      </Button>
    </div>
  )
}

ModelActions.propTypes = {
  id: string.isRequired,
  modelUrl: string.isRequired,
  isOnWishlist: bool.isRequired,
  handleWishlist: func.isRequired,
  isWishlistAvailable: bool.isRequired,
  isWishListActionLoading: bool,
  isOnCart: bool.isRequired,
  handleCart: func.isRequired,
  isCartAvailable: bool.isRequired,
  isCartActionLoading: bool,
  fullScreenGallery: bool,
  expandButtons: bool,
  isPurchased: bool,
  itemAttributes: object.isRequired,
  downloadData: shape({ filesHref: string, kind: string }),
  onDownload: func,
  locale: string,
}

const mapStateToProps = (context, { id }) => ({
  downloadData: context?.downloadAccess?.[id],
  onDownload: context?.openDownloadModal,
  locale: context?.locale,
})

export default WithListingPageContext(mapStateToProps)(ModelActions)
